import React from 'react'

interface LoadingSpinnerProps {
  message?: string
  submessage?: string
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'fullscreen' | 'inline'
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  message = 'Loading...',
  submessage,
  size = 'md',
  variant = 'default'
}) => {
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16'
  }

  const dotSizes = {
    sm: 'w-1.5 h-1.5',
    md: 'w-2 h-2',
    lg: 'w-2.5 h-2.5'
  }

  const SpinnerContent = () => (
    <div className="flex flex-col items-center justify-center">
      {/* Animated Logo/Spinner */}
      <div className={`relative ${sizeClasses[size]}`}>
        {/* Outer rotating ring */}
        <div className="absolute inset-0 rounded-xl bg-gradient-to-tr from-primary via-cyan-400 to-primary animate-spin" 
          style={{ animationDuration: '2s' }} />
        
        {/* Inner background */}
        <div className="absolute inset-1 rounded-lg bg-background" />
        
        {/* Center icon */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative">
            {/* Pulsing book icon */}
            <span className={`${size === 'sm' ? 'text-lg' : size === 'md' ? 'text-2xl' : 'text-3xl'} animate-pulse`}>
              📚
            </span>
          </div>
        </div>
      </div>

      {/* Loading dots */}
      <div className="flex gap-1.5 mt-4">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={`${dotSizes[size]} rounded-full bg-primary animate-bounce`}
            style={{ animationDelay: `${i * 0.15}s`, animationDuration: '0.6s' }}
          />
        ))}
      </div>

      {/* Message */}
      {message && (
        <p className={`mt-4 font-semibold text-foreground ${size === 'sm' ? 'text-sm' : 'text-base'}`}>
          {message}
        </p>
      )}
      
      {/* Submessage */}
      {submessage && (
        <p className={`mt-1 text-muted-foreground ${size === 'sm' ? 'text-xs' : 'text-sm'}`}>
          {submessage}
        </p>
      )}
    </div>
  )

  if (variant === 'fullscreen') {
    return (
      <div className="min-h-screen bg-gradient-space flex items-center justify-center">
        <div className="glass-card p-8 text-center">
          <SpinnerContent />
        </div>
      </div>
    )
  }

  if (variant === 'inline') {
    return (
      <div className="flex items-center justify-center py-4">
        <SpinnerContent />
      </div>
    )
  }

  return (
    <div className="py-12 text-center">
      <SpinnerContent />
    </div>
  )
}

// Simple spinner for buttons or small areas
export const MiniSpinner: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`relative w-5 h-5 ${className}`}>
    <div className="absolute inset-0 rounded-full border-2 border-current border-t-transparent animate-spin" />
  </div>
)

export default LoadingSpinner
